import type NotificationProvider from '$lib/components/notification/store.svelte';

import { browser } from '$app/environment';
import { goto } from '$app/navigation';
import AuthProvider from '$lib/components/auth/store.svelte';
import { m } from '$lib/paraglide/messages';
import { untrack } from 'svelte';

// Deep object copy of Cart
const deepCopy = (cart: ICart) => JSON.parse(JSON.stringify(cart));

const LOCALE_STORAGE_KEY = 'cart';
const LOCALE_STORAGE_EXP_KEY = 'cart_exp';
const EXP_DELAY_HOURS = 24;

const getNewExpirationDate = () => {
	const d = new Date();
	d.setHours(d.getHours() + EXP_DELAY_HOURS);
	return d.toISOString();
};

const isExpired = (exp: string | null) => exp !== null && new Date(exp).getTime() < Date.now();

export interface IAddress {
	lastname: string;
	firstname: string;
	company: string;
	street: string;
	postcode: string;
	city: string;
	region: string | null;
	country_code: string;
	phone_number: string;
}

export interface ICartItem {
	sku: string;
	name: string;
	brand: string;
	mpn: string;
	image: string;
	color: string;
	variation: string | null;
	slug: string;
	quantity: number;
	unit_price: number;
	unit_price_excl_tax: number;
	price: number;
	discount_amount: number;
	discount_tax_amount: number;
	quantity_sales_pack: number;
	in_stock: boolean;
	options: ICartItemOption[] | null;
}

export interface ICartItemOption {
	id: number;
	title: string;
	value_id: number | null;
	value: string;
}

export interface ICart {
	id?: number;
	billing_address?: IAddress;
	shipping_address?: IAddress;
	items_count?: number;
	items_quantity?: number;
	items: ICartItem[];
	subtotal?: number;
	total?: number;
	discount_amount?: number;
	coupon_code?: string;
	currency?: string;
	created_at?: Date;
	updated_at?: Date;
}

export type CartDiffDetails = Record<
	string,
	{ before?: string | number; after?: string | number; product_name?: string }
>;
export type CartDiffItem = { state: 'updated' | 'deleted' | 'same'; details: CartDiffDetails };
export type CartDiff = Record<string, CartDiffItem>; // { sku: ... }

function cartItemFromProduct(
	product: Product,
	sku: string,
	options: ICartItemOption[] | null,
	optionsPrice: number,
	quantity: number,
) {
	const variant = product.variants.find((v) => v.data.sku === sku)!;

	const unitPrice = (variant.special_price ?? variant.price)! + optionsPrice;

	return {
		sku: sku,
		name: product.data.name,
		brand: product.data.brand.name,
		mpn: variant.data.mpn,
		image: product.data.images[0],
		color: product.data.color,
		variation: variant.variation,
		slug: product.data.slug,
		quantity: quantity,
		unit_price: unitPrice,
		price: unitPrice * quantity,
		quantity_sales_pack: product.data.quantity_sales_pack,
		in_stock: variant.in_stock,
		options,
	} as ICartItem;
}

export class CartProvider {
	#cart = $state<ICart>({ items: [] });
	#cartDiff = $state<CartDiff | null>(null);

	#isAuthenticated = $state(false);
	#isLoading = $state(true);

	/**
	 * When user logs out, cart properties from Api (items_count, items_quantity, total, subtotal)
	 * get left behind and become stale since they're returned from the API and not calculated locally.
	 * This behavior is fixed by the clearLocal() method being called on proper logout.
	 *
	 * Forcing auth-check and re-calculation in the following variables handles the case where a user
	 * closes the browser while authenticated, then returns later without re-authenticating, thus not
	 * clearing their cart properly. This leaves behind stale values which override local values and
	 * prevent these variables from being updated.
	 */
	#subtotal = $derived(
		this.#isAuthenticated && this.#cart?.subtotal !== undefined
			? Number(this.#cart?.subtotal)
			: this.#cart.items.reduce((acc, item: ICartItem) => acc + this.itemSubtotal(item), 0),
	);
	#total = $derived(
		this.#isAuthenticated && this.#cart?.total !== undefined
			? Number(this.#cart?.total)
			: this.#subtotal + (this.#cart?.discount_amount ?? 0),
	);
	#itemsQuantity = $derived(
		this.#isAuthenticated && this.#cart?.items_quantity !== undefined
			? Number(this.#cart?.items_quantity)
			: (this.#cart.items.reduce((acc, item) => acc + item.quantity, 0) ?? 0),
	);

	#auth: AuthProvider;
	#notification: NotificationProvider;
	#storeId: number;
	#storeLang: string;
	#storeCountryCode: string;

	constructor(
		auth: AuthProvider,
		notification: NotificationProvider,
		storeId: number,
		locale: string,
	) {
		this.#auth = auth;
		this.#notification = notification;
		this.#storeId = storeId;
		[this.#storeLang, this.#storeCountryCode] = locale.split('_');

		$effect(() => {
			if (this.cartDiffDeletedProductNames.length) {
				goto('/cart').then(() => {
					this.#notification.add({
						type: 'error',
						message: m['cart_errors.deleted']({
							nbDeleted: this.cartDiffDeletedProductNames.length,
							products: this.cartDiffDeletedProductNames.join(', '),
						}),
					});
				});
			}
		});

		/**
		 * When init;
		 * When user loggin/loggout;
		 *
		 * Then reload cart from sources
		 */
		$effect(() => {
			if (!browser) return;

			untrack(() => {
				if (this.#isAuthenticated && !this.#auth.isAuthenticated) {
					// Clear local cart if loggout
					this.clearLocal();
				}
			});

			this.#isAuthenticated = this.#auth.isAuthenticated;

			untrack(() => this.load());
		});
	}

	/**
	 * Load cart data from sources: localStorage & Transactional
	 *
	 * Try first to get local cart from localStorage.
	 * If authenticated then try to get cart from transactional.
	 *
	 * A cart from localStorage override completly the transactional one.
	 *
	 * @returns
	 */
	async load() {
		this.#isLoading = true;
		const cartFromLocal = this.#loadFromLocalStorage();

		if (this.#isAuthenticated) {
			if (cartFromLocal && cartFromLocal.items.length) {
				// Force sync local into persistent
				const couponCode = cartFromLocal.coupon_code;
				await this.#save(cartFromLocal);

				if (couponCode) {
					await this.setCoupon(couponCode);
				}

				this.#isLoading = false;

				return;
			}

			// Get persistent cart
			const cartFromTransac = await this.#loadFromTransactionalApi().catch(() => null);
			if (cartFromTransac && cartFromTransac.items.length) {
				this.#cart = cartFromTransac;
				this.#saveToLocalStorage();
				this.#isLoading = false;
				return;
			}
		}

		await this.#save(cartFromLocal ?? this.#cart);
		this.#isLoading = false;
	}

	/**
	 * In all cases save the cart into localStorage.
	 * Try to save into transactional server, if an error occurred then rollback to the previous cart.
	 *
	 * TODO: Save into transactional can give an altered response. Show the changes to the user.
	 *
	 * @param tryCart
	 */
	async #save(tryCart: ICart) {
		const oldCart = this.#cart;
		this.#cart = tryCart;
		try {
			let response = tryCart;
			if (this.#isAuthenticated) {
				response = await this.#saveToTransactionalApi();
			} else if (oldCart.coupon_code !== tryCart.coupon_code || tryCart.coupon_code) {
				response = (await this.#emulateCoupon(tryCart)) as ICart;

				if (!response.coupon_code) {
					response = { items: response.items };
				}
			}

			this.setCart(response);
		} catch (e: unknown) {
			console.error(e);
			this.#cart = oldCart;
		}
	}

	setCart(cart: ICart) {
		if (!cart.coupon_code) {
			cart.coupon_code = undefined;
		}

		this.#cartDiff = this.#diffBetweenLocalAndTransac(this.#cart, cart);

		this.#cart = cart;

		if (this.isEmpty) {
			// A way to reset metadata of cart
			this.clearLocal();
		}

		this.#saveToLocalStorage();
	}

	async #emulateCoupon(cart: ICart): Promise<ICart> {
		return await (
			await fetch(`/api/transactional/cart/emulate`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					items: cart.items.map((item) => ({
						sku: item.sku,
						quantity: item.quantity,
						options:
							item.options && item.options.length
								? item.options.map((option) => ({
										id: option.id,
										value: String(option.value_id ?? option.value),
									}))
								: null,
					})),
					coupon_code: cart.coupon_code,
					store_id: this.#storeId,
					lang: this.#storeLang,
				}),
			})
		).json();
	}

	#loadFromLocalStorage(): ICart | null {
		const json = localStorage.getItem(LOCALE_STORAGE_KEY);

		if (!json || isExpired(localStorage.getItem(LOCALE_STORAGE_EXP_KEY))) {
			this.clearLocal();
			return null;
		}

		return JSON.parse(json) as ICart;
	}

	#saveToLocalStorage() {
		localStorage.setItem(LOCALE_STORAGE_KEY, JSON.stringify(this.#cart));
		localStorage.setItem(LOCALE_STORAGE_EXP_KEY, getNewExpirationDate());
	}

	clearLocal() {
		localStorage.removeItem(LOCALE_STORAGE_KEY);
		localStorage.removeItem(LOCALE_STORAGE_EXP_KEY);
		this.#cart = { items: [] };
	}

	async #loadFromTransactionalApi() {
		const response = await this.#auth.fetch(`/api/transactional/cart`);

		if (response.status !== 200) {
			// TODO: message error ?
			throw new Error(JSON.stringify(await response.text()));
		}

		return (await response.json()) as ICart;
	}

	async #saveToTransactionalApi() {
		const response = await this.#auth.fetch(`/api/transactional/cart`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				items: this.#cart.items.map((item) => ({
					sku: item.sku,
					quantity: item.quantity,
					options:
						item.options && item.options.length
							? item.options.map((option) => ({
									id: option.id,
									value: String(option.value_id ?? option.value),
								}))
							: null,
				})),
			}),
		});

		if (response.status !== 200) {
			// TODO: message error ?
			throw new Error(JSON.stringify(await response.text()));
		}

		return (await response.json()) as ICart;
	}

	async removeItem(sku: string, options: ICartItemOption[] | null) {
		const currIdx = this.#cart.items.findIndex(
			(value) => value.sku === sku && this.#areOptionsEqual(value.options, options),
		);
		if (currIdx !== -1) {
			const newCart = deepCopy(this.#cart);
			newCart.items.splice(currIdx, 1);
			await this.#save(newCart);
		}
	}

	async setItemQty(
		sku: string,
		quantity = 1,
		options: ICartItemOption[] | null,
		optionsPrice?: number,
		product?: Product,
	) {
		if (quantity <= 0) {
			return this.removeItem(sku, options);
		}

		const currIdx = this.#cart.items.findIndex(
			(value) => value.sku === sku && this.#areOptionsEqual(value.options, options),
		);
		const newCart = deepCopy(this.#cart);
		if (currIdx !== -1) {
			newCart.items[currIdx].quantity = quantity;
			newCart.items[currIdx].price = newCart.items[currIdx].unit_price * quantity;
		} else if (product && optionsPrice !== undefined) {
			newCart.items.push(cartItemFromProduct(product, sku, options, optionsPrice, quantity));
		}
		await this.#save(newCart);
	}

	async addItemQty(
		product: Product,
		sku: string,
		options: ICartItemOption[],
		optionsPrice: number,
		quantity = 1,
	) {
		const currIdx = this.#cart.items.findIndex(
			(value) => value.sku === sku && this.#areOptionsEqual(value.options, options),
		);
		await this.setItemQty(
			sku,
			currIdx !== -1 ? this.#cart.items[currIdx].quantity + quantity : quantity,
			options,
			optionsPrice,
			product,
		);
	}

	/**
	 * Cart from transac is the truth
	 * So generate a diff between local and transac
	 *
	 * @param cartLocal
	 * @param cartTransac
	 */
	#diffBetweenLocalAndTransac(cartLocal: ICart, cartTransac: ICart): CartDiff | null {
		const diff: CartDiff = {};
		let haveDiff = false;

		cartTransac.items.forEach((item: ICartItem) => {
			const localItem = cartLocal.items.find(
				(i) => i.sku === item.sku && this.#areOptionsEqual(i.options, item.options),
			);
			if (!(item.sku in diff)) diff[item.sku] = { state: 'updated', details: {} };

			if (localItem) {
				let data = {
					before: localItem.unit_price ?? 0,
					after: item.unit_price ?? 0,
				};
				if (data.before - data.after > 0.01) {
					diff[item.sku].details['price'] = data;
					haveDiff = true;
				}

				data = { before: localItem.quantity, after: item.quantity };
				if (data.before !== data.after) {
					diff[item.sku].details['quantity'] = data;
					haveDiff = true;
				}

				diff[item.sku].state = haveDiff ? 'updated' : 'same';
			}
		});

		cartLocal.items.forEach((item: ICartItem) => {
			if (!(item.sku in diff)) {
				diff[item.sku] = { state: 'deleted', details: { quantity: { product_name: item.name } } };
				haveDiff = true;
			}
		});

		return haveDiff ? diff : null;
	}

	/**
	 * Sync current cart in order to check diff (ie: before checkout)
	 */
	async sync() {
		await this.#save(this.#cart);
	}

	async setCoupon(code: string, silent: boolean = true) {
		this.#isLoading = true;

		if (this.#isAuthenticated) {
			const response = await this.#auth.fetch(`/api/transactional/cart/coupon`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ code }),
			});

			if (response.ok) {
				this.setCart(await response.json());
			}
		} else {
			const newCart = deepCopy(this.#cart);
			newCart.coupon_code = code;
			await this.#save(newCart);
		}

		this.#isLoading = false;

		if (this.couponCode === code && !silent) {
			this.#notification.add({
				type: 'success',
				message: m['discount_code_messages.applied'](),
			});
		} else if (this.couponCode !== code) {
			this.#notification.add({
				type: 'error',
				message: m['discount_code_messages.invalid'](),
			});
		}
	}

	async removeCoupon() {
		this.#isLoading = true;
		if (this.#isAuthenticated) {
			const response = await this.#auth.fetch(`/api/transactional/cart/coupon`, {
				method: 'DELETE',
			});

			if (response.ok) {
				this.setCart(await response.json());
			}
		} else {
			const newCart = deepCopy(this.#cart);
			newCart.coupon_code = undefined;
			await this.#save(newCart);
		}
		this.#isLoading = false;

		if (!this.couponCode) {
			this.#notification.add({
				type: 'success',
				message: m['discount_code_messages.removed'](),
			});
		} else {
			this.#notification.addUnknown();
		}
	}

	async getRecommendations() {
		const params = new URLSearchParams();

		this.items.forEach((item) => {
			params.append('skus', item.sku);
		});

		const response = await fetch(`/api/content/recommend/cart-completion?${params}`, {
			headers: {
				'X-Store-ID': this.#storeId.toString(),
			},
		});

		if (response.status !== 200) {
			return [];
		}

		return (await response.json()) as ProductFromCategory[];
	}

	#areOptionsEqual(optionsA: ICartItemOption[] | null, optionsB: ICartItemOption[] | null) {
		if (optionsA?.length === 0) {
			optionsA = null;
		}

		if (optionsB?.length === 0) {
			optionsB = null;
		}

		if (optionsA === optionsB) return true;
		if (!optionsA || !optionsB) return false;
		if (optionsA.length !== optionsB.length) return false;

		const sortFn = (a: ICartItemOption, b: ICartItemOption) => a.id - b.id;

		const sortedA = [...optionsA].sort(sortFn);
		const sortedB = [...optionsB].sort(sortFn);

		for (let i = 0; i < sortedA.length; i++) {
			if (sortedA[i].id !== sortedB[i].id || sortedA[i].value !== sortedB[i].value) {
				return false;
			}
		}
		return true;
	}

	get items() {
		return this.#cart.items;
	}
	get subtotal() {
		return this.#subtotal;
	}
	get discount() {
		return this.#cart.discount_amount ?? 0;
	}
	get total() {
		return this.#total;
	}
	get itemsQuantity() {
		return this.#itemsQuantity;
	}
	get createdAt() {
		return this.#cart.created_at;
	}
	get billingAddress() {
		return this.#cart.billing_address;
	}
	get shippingAddress() {
		return this.#cart.shipping_address;
	}
	get couponCode() {
		return this.#cart?.coupon_code;
	}

	get cartDiff() {
		return this.#cartDiff;
	}
	get cartDiffDeletedProductNames() {
		if (!this.#cartDiff) return [];

		const productNames = [];
		for (const sku in this.#cartDiff) {
			if (this.#cartDiff[sku].state === 'deleted') {
				productNames.push(this.#cartDiff[sku].details.quantity.product_name);
			}
		}

		return productNames;
	}

	get isEmpty() {
		return this.#cart.items.length === 0;
	}
	get isLoading() {
		return this.#isLoading;
	}

	get isCountryDifferent() {
		return (
			this.#cart.shipping_address &&
			this.#storeCountryCode !== this.#cart.shipping_address.country_code
		);
	}

	itemPrice(item: ICartItem) {
		return item.unit_price;
	}

	itemSubtotal(item: ICartItem) {
		return item.price;
	}
}

export default CartProvider;
